import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateBooking } from '../api';
import { useUserContext } from '../context/UserContext';
import { useNotifications } from '../context/NotificationContext';
import { useAnalytics } from '../context/AnalyticsContext';
import { useFeatureFlag } from '../context/FeatureFlagContext';
import { useNewsletterModalState } from './useNewsletterModalState';
import { FeatureFlags } from '../utils/featureFlags';
import { Camp, CampDate } from '../utils/types';

interface UseCampBookingOptions {
  camp: Camp | undefined;
  analyticsPrefix: string;
  newsletterEnabled?: boolean;
}

interface UseCampBookingReturn {
  showNewsletterModal: boolean;
  showRegistrationModal: boolean;
  pendingBookingDate: CampDate | null;
  isBookingPending: boolean;
  isInstantBooking: boolean;
  handleBookDate: (campDate: CampDate) => Promise<void>;
  handleNewsletterSignup: () => Promise<void>;
  handleSkipNewsletter: () => Promise<void>;
  handleNewsletterModalClose: () => void;
  handleRegister: (email: string) => void;
  handleLogin: (email: string) => void;
  setShowRegistrationModal: (show: boolean) => void;
}

export const useCampBooking = ({
  camp,
  analyticsPrefix,
  newsletterEnabled = false,
}: UseCampBookingOptions): UseCampBookingReturn => {
  const navigate = useNavigate();
  const createBooking = useCreateBooking();
  const { isLoggedIn } = useUserContext();
  const { showSuccess, showError } = useNotifications();
  const { trackEvent } = useAnalytics();
  const { shouldShowModal, markAsShown, markAsSignedUp } = useNewsletterModalState();
  const instantBookingEnabled = useFeatureFlag(FeatureFlags.INSTANT_BOOKING, false);

  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [pendingBookingDate, setPendingBookingDate] = useState<CampDate | null>(null);

  const isInstantBooking = instantBookingEnabled && (camp?.organizer?.instant_booking ?? false);

  const processBooking = async (campDate: CampDate) => {
    if (!camp) return;

    if (!isInstantBooking) {
      if (camp.website) {
        window.open(camp.website, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    try {
      const booking = await createBooking.mutateAsync({
        camp_id: camp.id,
        camp_date_ids: [String(campDate.id)]
      });
      navigate(`/checkout?booking_uuid=${booking.uuid}`, { state: { booking } });
    } catch (error) {
      showError('Failed to create booking. Please try again.', error as Error);
    }
  };

  const handleBookDate = async (campDate: CampDate) => {
    if (!camp) return;
    trackEvent(`${analyticsPrefix}_date_book_now_clicked`, { camp_id: camp.id, camp_date_id: campDate.id });

    if (!isLoggedIn && newsletterEnabled && shouldShowModal() && !isInstantBooking) {
      setPendingBookingDate(campDate);
      setShowNewsletterModal(true);
      return;
    }

    await processBooking(campDate);
  };

  const handleNewsletterSignup = async () => {
    if (!camp || !pendingBookingDate) return;

    setShowNewsletterModal(false);
    markAsSignedUp();
    trackEvent(`${analyticsPrefix}_newsletter_signup`, { camp_id: camp.id });
    showSuccess('Thank you for subscribing to our newsletter!');

    await processBooking(pendingBookingDate);
    setPendingBookingDate(null);
  };

  const handleSkipNewsletter = async () => {
    if (!camp || !pendingBookingDate) return;

    setShowNewsletterModal(false);
    markAsShown();
    trackEvent(`${analyticsPrefix}_newsletter_skip`, { camp_id: camp.id });

    await processBooking(pendingBookingDate);
    setPendingBookingDate(null);
  };

  const handleNewsletterModalClose = () => {
    if (!camp) return;

    setShowNewsletterModal(false);
    markAsShown();
    trackEvent(`${analyticsPrefix}_newsletter_modal_closed`, { camp_id: camp.id });
    setPendingBookingDate(null);
  };

  const handleRegister = (email: string) => {
    setShowRegistrationModal(false);
    showSuccess("You have successfully registered! You can now add favorites and add camps to your schedule.");
  };

  const handleLogin = (email: string) => {
    setShowRegistrationModal(false);
    showSuccess("You have successfully logged in! You can now add favorites and add camps to your schedule.");
  };

  return {
    showNewsletterModal,
    showRegistrationModal,
    pendingBookingDate,
    isBookingPending: createBooking.isPending,
    isInstantBooking,
    handleBookDate,
    handleNewsletterSignup,
    handleSkipNewsletter,
    handleNewsletterModalClose,
    handleRegister,
    handleLogin,
    setShowRegistrationModal,
  };
};
